import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useSessionStore } from './SessionStore';

/**
 * Permission store — derives the signed-in user's effective permission/role set from the
 * session profile and exposes guard helpers. Consumed by route guards (`PermissionRoute`),
 * UI gates (`<Can>`), and the nav config. Mirrors the backend `[HasPermission]` codes.
 */
interface PermissionStoreValue {
  permissions: string[];
  roles: string[];
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (codes: string[]) => boolean;
  hasAllPermissions: (codes: string[]) => boolean;
}

const PermissionContext = createContext<PermissionStoreValue | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { profile } = useSessionStore();

  const permSet = useMemo(() => new Set(profile?.permissions ?? []), [profile]);

  const hasPermission = useCallback((code: string) => permSet.has(code), [permSet]);
  const hasAnyPermission = useCallback(
    (codes: string[]) => codes.length === 0 || codes.some((c) => permSet.has(c)),
    [permSet],
  );
  const hasAllPermissions = useCallback(
    (codes: string[]) => codes.every((c) => permSet.has(c)),
    [permSet],
  );

  const value = useMemo<PermissionStoreValue>(
    () => ({
      permissions: profile?.permissions ?? [],
      roles: profile?.roles ?? [],
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
    }),
    [profile, hasPermission, hasAnyPermission, hasAllPermissions],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePermissionStore() {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissionStore must be used within a PermissionProvider');
  return ctx;
}
